import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import InputSingleDigitColumns, {
  InputSingleDigitColumnsAnswer,
} from '~/bootcamp/Activities/components/InputSingleDigitColumns'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress, randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { range } from '~/utils/helpers'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { getRanges } from './constants'
import InputRowWrapper from './components/InputRowWrapper'
import Expression from './components/Expression'

export default function LongMulti1({
  answerState,
  onAnswerStateChange,
  difficultyLevel,
  onNextQuestionClick,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState<InputSingleDigitColumnsAnswer>({})
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { multiplicandMin, multiplicandMax, multiplierLen } = useMemo(() => {
    return getRanges('LONG_MULTI_1', difficultyLevel)
  }, [difficultyLevel])

  const [multiplier, multiplicand, maxAnswerLen] = useMemo(() => {
    const generate = (): { _multiplier: number; _multiplicand: number } => {
      const _multiplicand = randomIntFromInterval(multiplicandMin, multiplicandMax)
      const _multiplier = generateMultiplier(_multiplicand, multiplierLen)
      const product = _multiplier * _multiplicand

      if (taskHistory.includes(product)) {
        return generate()
      } else {
        return {
          _multiplier,
          _multiplicand,
        }
      }
    }

    const { _multiplier, _multiplicand } = generate()
    const _maxAnswerLen = multiplierLen + 1

    setAnswer(generateInitialState(_maxAnswerLen))
    return [String(_multiplier), String(_multiplicand), _maxAnswerLen]
  }, [multiplierLen, multiplicandMax, multiplicandMin, taskHistory])

  // Only the two factors — the worked columns are the answer.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const trueAnswer = Number(multiplier) * Number(multiplicand)
    const enteredNumber = Number(Object.values(answer).join(''))
    const isCorrect = trueAnswer === enteredNumber
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [Number(Object.values(answer).join('')), ...prevState].slice(0, multiplierLen * 10)
    })

    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const handleOnRemoveButtonClick = (): void => {
    setAnswer(generateInitialState(maxAnswerLen))
  }

  const handleOnChange = (value: string, name: string): void => {
    setAnswer((prevState) => ({ ...prevState, [name]: value }))
  }

  const onEnterPress = (): void => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const commonGridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${maxAnswerLen + 1}, minmax(0, 1fr))`,
  }

  const inputGridStyle = {
    gridColumnStart: 2,
    gridColumnEnd: maxAnswerLen + 2,
  }

  const isFormValid = (): boolean => {
    return Object.keys(answer).some((key: string) => answer[key] !== '')
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper className="space-y-1 flex-col">
        <Expression maxAnswerLen={maxAnswerLen} multiplicand={multiplicand} multiplier={multiplier} />
        <Divider />
        <InputRowWrapper className="justify-between">
          <Equals />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertProduct')}
              autoFocus
              id={`LongMulti1:${inputKey}`}
              isError={answerState === false}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              ref={inputRef}
              style={inputGridStyle}
              values={answer}
            />
          </div>
        </InputRowWrapper>
        <Divider />
        <Divider />
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid(),
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: handleOnRemoveButtonClick,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateInitialState(maxAnswerLen: number): { [key: string]: string } {
  return range(maxAnswerLen).reduce(
    (acc, currValue) => ({
      ...acc,
      [`digit-${currValue + 1}`]: '',
    }),
    {},
  )
}

function generateMultiplierDigit(multiplicand: number): number {
  const num = randomIntFromInterval(1, 9)

  if (num * multiplicand > 9) {
    return generateMultiplierDigit(multiplicand)
  } else {
    return num
  }
}

function generateMultiplier(multiplicand: number, digitsCount: number): number {
  const numArr = range(digitsCount).map(() => generateMultiplierDigit(multiplicand))
  return parseInt(numArr.join(''), 10)
}
